import type { FragmentType } from '@packages/gql/generated/shopping';
import { getFragmentData, graphql } from '@packages/gql/generated/shopping';
import {
  CriteoProductsRecoSlider,
  PrudsysProductsRecoSlider,
  SeriesProductsRecoSlider,
} from '@packages/recommendations';
import { Stack, Typography } from '@packages/shared';
import { useIntl } from 'react-intl';
import { useHashParameters } from '../../hooks/useHashParameters';
import { SlotBox } from '../SlotBox';
import { fallbackRDEHeadlines } from './messages';

const RecommendationsFragment = graphql(/* GraphQL */ `
  fragment RecommendationsFragment on Product {
    hasSeries
    defaultCategory
    promotion
    orderNumber
    brand {
      name
    }
    availability {
      state
    }
    price {
      value
    }
  }
`);

export type RecommendationsProps = {
  recosData: FragmentType<typeof RecommendationsFragment>;
};

/**
 * The `Recommendation` component is used as a slot, which bundles different recommendation sliders.
 *
 * As of now the sliders show recommendations for:
 * <ul>
 *  <li>"Andere Kunden interessierte auch - Prudsys"</li>
 *  <li>"Mehr aus dieser Serie - "</li>
 *  <li>"Gesponsorte Artikel - Criteo"</li>
 * </ul>
 *
 * This component is used as part of the detailview-grid.
 * */
export const Recommendations = ({ recosData }: RecommendationsProps) => {
  const { orderNumber, brand, defaultCategory, hasSeries, promotion, availability, price } =
    getFragmentData(RecommendationsFragment, recosData);

  const { category = '' } = useHashParameters();
  const { formatMessage } = useIntl();

  return (
    <>
      <Typography component="header" variant="h2" sx={{ m: 2 }}>
        {formatMessage(fallbackRDEHeadlines.sectionTitle)}
      </Typography>
      <SlotBox>
        <Stack direction="column" spacing={6}>
          <PrudsysProductsRecoSlider
            brand={brand?.name ?? ''}
            categoryIds={[defaultCategory ?? category]}
            productId={orderNumber}
            pageTemplate="detailview"
            fetchOnThreshold
            elementIndex={0}
            title={formatMessage(fallbackRDEHeadlines.prudSysFallback)}
            key={`${orderNumber}${0}`}
          />
          {hasSeries && (
            <SeriesProductsRecoSlider
              productId={`${orderNumber}${promotion}`}
              elementIndex={1}
              fetchOnThreshold
              pageTemplate="detailview"
              title={formatMessage(fallbackRDEHeadlines.seriesFallback)}
              key={`${orderNumber}${1}`}
            />
          )}
          <CriteoProductsRecoSlider
            available={availability.state === 'NOT_AVAILABLE' ? 0 : 1}
            price={parseFloat(price.value.toFixed(2))}
            productId={orderNumber}
            fetchOnThreshold
            elementIndex={hasSeries ? 2 : 1}
            pageTemplate="detailview"
            title={formatMessage(fallbackRDEHeadlines.criteoFallback)}
            key={`${orderNumber}${2}`}
          />
        </Stack>
      </SlotBox>
    </>
  );
};
